import * as React from 'react';
import { BaseRouteProps } from '../../../interfaces';
import { computed } from 'mobx';
import { observer, inject } from 'mobx-react';
import { QuadrantGrid, QuadrantGridData, PVAPage, PVATeamSelect, LoadingIndicatorMini, PVASelect, PVASelectOption, SecondaryScale, SecondaryScaleData, PVACharacteristicSelect } from '../../../components';
import {
    ReportDataService,
    CharacteristicDataService,
    GridOptionDataService,
    CharacteristicGroupDataService,
    UserDataService
} from '../../../services';
import { TeamGridService } from '../../../services/api/TeamGridService';
import { User, ExtendedTeam, Characteristic, GridOption, CharacteristicGroup, Team, TeamGrid, UserPermissions } from '../../../models';
import { UserStore, FilterStore } from '../../../stores';
import { Roles } from '../../../stores/Constants';
import { hasPermission } from '../../../utilities/User';

class TeamGridScore {
    characteristicID: number;
    id: number;
    score: number;
    userID: number;
}

interface TeamGridReportState {
    orgId: number;
    surveyId: number;
    surveyVersionId: number;
    selectedUserData: any[];
    usersToFocus: User[];
    reportType: number;
    selectedCharacteristic: Characteristic[];
    characteristics: Characteristic[];
    characteristicGroups: CharacteristicGroup[];
    miniLoad: boolean;
    gridOptions: GridOption[];
    listView: boolean;
    open: boolean;
    customScaleX: number;
    customScaleY: number;
    scores: TeamGridScore[];
    teamId: number;
}

interface TeamGridReportProps extends BaseRouteProps<{}> {
    UserStore?: UserStore;
    FilterStore?: FilterStore;
}

@inject('UserStore', 'FilterStore') @observer
export class TeamGridReport extends React.Component<TeamGridReportProps, TeamGridReportState> {
    private static readonly ReportTypes = {
        CustomScaleComparison: -2,
    };

    private reportDataService = new ReportDataService(this.props.notificationService);
    private characteristicDataService = new CharacteristicDataService(this.props.notificationService);
    private characteristicGroupDataService = new CharacteristicGroupDataService(this.props.notificationService);
    private gridOptionDataService = new GridOptionDataService(this.props.notificationService);

    constructor(props: TeamGridReportProps) {
        super(props);

        this.state = {
            selectedUserData: [],
            usersToFocus: [],
            reportType: 0,
            orgId: 0,
            characteristics: [],
            characteristicGroups: [],
            surveyId: 0,
            surveyVersionId: 0,
            selectedCharacteristic: [],
            miniLoad: false,
            gridOptions: [],
            listView: false,
            open: false,
            customScaleX: 0,
            customScaleY: 0,
            scores: [],
            teamId: 0,
        };
    }

    @computed get organization() {
        if (this.props.FilterStore && this.props.FilterStore.organization) {
            return this.props.FilterStore.organization;
        }
        return null;
    }

    @computed get survey() {
        if (this.props.FilterStore && this.props.FilterStore.survey) {
            return this.props.FilterStore.survey;
        }
        return null;
    }

    @computed get team() {
        if (this.props.FilterStore && this.props.FilterStore.team) {
            return this.props.FilterStore.team;
        }
        return null;
    }

    @computed get teams() {
        if (this.props.FilterStore && this.props.FilterStore.teams) {
            return this.props.FilterStore.teams;
        }
        return [];
    }

    @computed get users() {
        if (this.props.FilterStore && this.props.FilterStore.users) {
            return this.props.FilterStore.users.filter(u => !u.resultsArePrivate && !hasPermission(u.userPermissions, UserPermissions.Foundations));
        }
        return [];
    }

    @computed get teamId() {
        return this.props.FilterStore!.teamId;
    }

    @computed get surveyVersion() {
        if (this.props.FilterStore && this.props.FilterStore.surveyVersion) {
            return this.props.FilterStore.surveyVersion;
        }
        return null;
    }

    @computed get userRole() {
        return this.props.UserStore!.userInfo.userRole;
    }

    @computed get userPermissions() {
        return this.props.UserStore!.userInfo.userPermissions;
    }

    private async updateSurveyVersion() {
        if (!this.surveyVersion) {
            return;
        }

        this.gridOptionDataService.getAllBySurveyVersion(this.surveyVersion, this.organization ? this.organization : undefined).then((gridOpts: GridOption[]) => {
            this.setState({
                gridOptions: gridOpts
            });
        });

        this.characteristicGroupDataService.getAllBySurveyVersion(this.surveyVersion).then((characteristicGroups: CharacteristicGroup[]) => {
            this.setState({
                characteristicGroups: characteristicGroups
            });
        });

        let characteristics = await this.characteristicDataService.getAllBySurveyVersion(this.surveyVersion);
        {
            this.setState({
                characteristics: characteristics
            });

            if (this.isQuadrantType(this.state.reportType) && this.surveyVersion && this.surveyVersion.teamGrids && this.surveyVersion.teamGrids.length > 0) {
                var tg = this.getCustomQuadrantIdMapping()[this.state.reportType];

                var one = characteristics.find((x: Characteristic) => {
                    return x.id === tg.quadrantOneID;
                });
                if (one === undefined) {
                    return;
                }

                var two = characteristics.find((x: Characteristic) => {
                    return x.id === tg.quadrantTwoID;
                });
                if (two === undefined) {
                    return;
                }

                var three = characteristics.find((x: Characteristic) => {
                    return x.id === tg.quadrantThreeID;
                });
                if (three === undefined) {
                    return;
                }

                var four = characteristics.find((x: Characteristic) => {
                    return x.id === tg.quadrantFourID;
                });
                if (four === undefined) {
                    return;
                }

                this.setState({
                    selectedCharacteristic: [one, two, three, four]
                });
            }
        }
    }

    private addScores(page: number, scores: any[]) {
        if (scores.length > 0) {
            var stateScores = this.state.scores;
            scores.map((s: any) => {
                stateScores.push(s);
            });
            this.setState(
                {
                    scores: stateScores
                },
                () => {
                    this.loadTeamGrid(page + 1);
                });
        } else {
            this.setState(
                {
                    miniLoad: false
                },
                this.forceUpdate);
        }
    }

    private loadTeamGrid(page: number) {
        const pageSize = 1000;
        if (!this.surveyVersion) {
            return;
        }

        var surveyVersionId = this.surveyVersion.id;
        if (this.teamId > 0) {
            this.reportDataService.getTeamGrid(this.teamId, surveyVersionId, page, pageSize).then((scores: any[]) => {
                this.addScores(page, scores);
            });
        } else if (this.teamId === -100 && this.organization) {
            this.reportDataService.getTeamGridByOrg(this.organization.id, surveyVersionId, page, pageSize).then((scores: any[]) => {
                this.addScores(page, scores);
            });
        } else if (this.teamId === -200 && this.organization) {
            this.reportDataService.getTeamGridByOrgCandidates(this.organization.id, surveyVersionId, page, pageSize).then((scores: any[]) => {
                this.addScores(page, scores);
            });
        } else if (this.teamId === -300 && this.organization) {
            this.reportDataService.getTeamGridByOrgFormer(this.organization.id, surveyVersionId, page, pageSize).then((scores: any[]) => {
                this.addScores(page, scores);
            });
        } else {
            this.setState(
                {
                    miniLoad: false
                },
                this.forceUpdate);
        }
    }

    private async updateReport() {
        if (!this.survey || !this.surveyVersion) {
            return;
        }

        this.setState({
            miniLoad: true
        });

        await this.updateSurveyVersion();

        this.setState(
            {
                scores: []
            }, 
            () => {
                this.loadTeamGrid(0);
            });
    }

    private async update() {
        if (this.userRole !== Roles.User) {
            if (this.organization && this.organization.id !== this.state.orgId) {
                this.setState({
                    selectedUserData: [],
                    miniLoad: false,
                    orgId: this.organization.id,
                    reportType: 0
                });
            }

            if (this.survey && this.state.surveyId !== this.survey.id) {
                this.setState(
                    {
                        surveyId: this.survey.id,
                        reportType: 0
                    },
                    this.updateReport);
            }

            if (this.surveyVersion && this.surveyVersion.id !== this.state.surveyVersionId) {
                this.setState(
                    {
                        surveyVersionId: this.surveyVersion.id,
                        reportType: 0
                    },
                    this.updateReport);                
            }
        }

        if (this.state.teamId !== this.teamId) {
            this.setTeamId(this.teamId);
            this.setState({teamId: this.teamId});
        }
    }

    async componentDidMount() {
        await this.update();
    }

    async componentWillReact() {
        await this.update();
    }

    private reportChange(e: any) {
        var reportId: number = Number(e.target.value);

        if (this.isQuadrantType(reportId)) {
            this.setState(
                {
                    listView: false, // quadrant types don't have list view
                    reportType: reportId
                },
                async () => { await this.updateReport(); await this.forceUpdate(); }
            );
        } else {
            this.setState(
                {
                    reportType: reportId
                }, 
                this.forceUpdate
            );
        }
        
    }

    private isQuadrantType(t: any) {
        var asFloat = parseFloat(t);
        return asFloat <= -1 && asFloat > -2;
    }

    public teamChange(e: any) {
        var teamId: number = Number(e.target.value);
        this.setTeamId(teamId);
    }

    public setTeamId(teamId: number) {
        if (teamId === 0) {
            this.setState(
                {
                    miniLoad: true
                },
                () => {
                    if (this.props.FilterStore) {
                        this.props.FilterStore.setTeamID(teamId).then(() => {
                            this.setState(
                                {
                                    miniLoad: false,
                                    selectedUserData: []
                                },
                                this.updateReport);
                        });
                    }
                });
            return;
        }
        var team = this.teams.find(x => x.id === teamId);
        if (team != null) {
            if (this.props.FilterStore) {
                this.setState(
                    {
                        miniLoad: true
                    }
                );
                this.props.FilterStore.setTeam(team).then(() => {
                    this.setState(
                        {
                            selectedUserData: []
                        },
                        this.updateReport);
                });
                this.props.FilterStore.teamId = teamId;
            }
        } else {
            this.setState(
                {
                    miniLoad: true
                },
                () => {
                    if (this.props.FilterStore) {
                        this.props.FilterStore.setTeamID(teamId).then(() => {
                            this.setState(
                                {
                                    miniLoad: false,
                                    selectedUserData: []
                                },
                                this.updateReport);
                        });
                    }
                });
        }
    }

    private ActionsButton() {
        return (
            <div className="btn-group dropdown">
                <button type="button" className="btn btn-primary"  onClick={(e) => this.setState({open: !this.state.open })}>
                    <span>Actions </span>
                    <span className="caret" />
                </button>
                <ul className={`action-menu dropdown-menu ${this.state.open ? 'open' : ''}`}>
                    <li className="options-header">Actions</li>
                    {!this.isQuadrantType(this.state.reportType) &&
                        <li className="options-entry" onClick={(e) => this.setState({listView: true, open: false})}>
                            <div className="options-entry-title">
                                <span className="glyphicon glyphicon-list-alt" /> <span> List View</span>
                            </div>
                            <div className="options-entry-desc">
                                Show the report as a list view
                            </div>
                        </li>
                    }
                    
                    <li className="options-entry" onClick={(e) => this.setState({listView: false, open: false})}>
                        <div className="options-entry-title">
                            <span className="glyphicon glyphicon-th" /> <span> Grid View</span>
                        </div>
                        <div className="options-entry-desc">
                            Show the report as a grid view
                        </div>
                    </li>
                    
                </ul>
        </div>);
    }


    private getScore(userId: number, charId: number) {
        var q1Val = this.state.scores.find((s: TeamGridScore) => {
            return s.userID === userId && s.characteristicID === charId;
        });
        return q1Val ? q1Val.score : 0;
    }

    indexOfMax(arr: number[]) {
        if (arr.length === 0) {
            return -1;
        }
    
        var max = arr[0];
        var maxIndex = 0;
    
        for (var i = 1; i < arr.length; i++) {
            if (arr[i] > max) {
                maxIndex = i;
                max = arr[i];
            }
        }
    
        return maxIndex;
    }

    public render() {
        const data: QuadrantGridData[] = [];
        // var availableUsers = this.state.selectedUserData;
        // var taken: any = {};

        var q1 = this.state.selectedCharacteristic[0];
        var q2 = this.state.selectedCharacteristic[1];
        var q3 = this.state.selectedCharacteristic[2];
        var q4 = this.state.selectedCharacteristic[3];
        
        if (q1 && q2 && q3 && q4) { // && accommodate
            this.users.map((u: User) => {
                var q1Val = this.getScore(u.id, q1.id); // top
                var q2Val = this.getScore(u.id, q2.id); // bottom
                var q3Val = this.getScore(u.id, q3.id); // left
                var q4Val = this.getScore(u.id, q4.id); // right

                var scoreX = 0;
                var scoreY = 0;
                var ind = 0;
                if (q1Val > q2Val) {
                    // top
                    if (q3Val > q4Val) {
                        // left
                        ind = 0;
                        scoreX = q3Val;
                        scoreY = q1Val;
                    } else {
                        // right
                        ind = 1;
                        scoreX = q4Val;
                        scoreY = q1Val;
                    }
                } else {
                    // bottom
                    if (q3Val > q4Val) {
                        // left
                        ind = 2;
                        scoreX = q3Val;
                        scoreY = q2Val;
                    } else {
                        // right
                        ind = 3;
                        scoreX = q4Val;
                        scoreY = q2Val;
                    }
                }
                // var accommodateVal = this.getScore(u.id, accommodate.id);

                // var scores = [q1Val, q2Val, q3Val, q4Val];
                // var ind = this.indexOfMax(scores);

                var d = new QuadrantGridData();
                d.value = scoreX * .85; // scores[ind];
                d.accommodate = scoreY * .85; // accommodateVal;
                d.label = u.firstName[0] + u.lastName[0];
                d.quadrant = ind;
                d.user = u;
                d.additionalClass = '';
                data.push(d);
            });
        }

        let defaultOptions: any[] = [{
            id: 0,
            value: '- Select -'
        }];
        
        if (!this.props.UserStore!.userRoleIn([Roles.Manager, Roles.User]) || this.props.UserStore!.checkUserPermission(UserPermissions.ViewAllUsers) ) {
            defaultOptions.push({
                id: -100,
                value: 'All Users'
            });
        }
        if (!this.props.UserStore!.userRoleIn([Roles.Manager, Roles.User]) || this.props.UserStore!.checkUserPermission(UserPermissions.ViewAllCandidates) ) {
            defaultOptions.push({
                id: -200,
                value: 'All Candidates'
            });
        }
        if (!this.props.UserStore!.userRoleIn([Roles.Manager, Roles.User]) || this.props.UserStore!.checkUserPermission(UserPermissions.ViewAllFormerEmployees) ) {
            defaultOptions.push({
                id: -300,
                value: 'All Former Employees'
            });
        }

        var reportOptions: PVASelectOption[] = [
            {
                id: 0,
                label: '- Select -'
            },
        ];

        var quadrantMap = this.getCustomQuadrantIdMapping();
        for (const [key, value] of Object.entries(quadrantMap)) {
            reportOptions.push({
                id: parseFloat(key),
                label: value.name
            });
        }

        if (this.props.UserStore!.userRoleIn([Roles.SuperAdmin, Roles.SiteAdmin, Roles.PonderaAssociate])) {
            reportOptions.push({id: TeamGridReport.ReportTypes.CustomScaleComparison, label: 'Custom Scale Comparison'});
        }

        this.state.gridOptions.map((gridOpt: GridOption) => {
            reportOptions.push({
                id: gridOpt.id,
                label: gridOpt.name
            });
        });

        var secondaryData: SecondaryScaleData[] = [];
        var selectedReport = this.state.gridOptions.find((go: GridOption) => {
            return go.id === this.state.reportType;
        });

        if (selectedReport) {
            this.users.map((u: User) => {
                var d = new SecondaryScaleData();
                d.label = '';
                if (selectedReport) {
                    d.valueX = this.getScore(u.id, selectedReport.characteristicOneID);
                    d.valueY = this.getScore(u.id, selectedReport.characteristicTwoID);
                }
                d.user = u;
                secondaryData.push(d);
            });
        }

        // custom scale report
        const customScaleData: SecondaryScaleData[] = [];
        if (this.state.reportType === TeamGridReport.ReportTypes.CustomScaleComparison
            && this.state.customScaleX > 0
            && this.state.customScaleY > 0) {
            
            this.users.map((u: User) => {
                var d = new SecondaryScaleData();
                d.label = '';
                d.valueX = this.getScore(u.id, this.state.customScaleX);
                d.valueY = this.getScore(u.id, this.state.customScaleY);
                d.user = u;
                customScaleData.push(d);
            });
        }

        return (
            <PVAPage>
                {this.organization && <div id={`organization-${this.organization.id}`} />}
                {this.survey && <div id={`survey-${this.survey.id}`} />}

                <PVAPage.Header>
                    <PVAPage.Header.Title>
                        <PVAPage.Header.Title.Text>
                            <span>PVA Grid Report<span className="trademark">TM</span></span>
                            <span className="light"> for </span>
                            <PVATeamSelect disabled={this.state.miniLoad} defaultOptions={defaultOptions} options={this.teams} selected={this.teamId} onChange={(e: any) => { this.teamChange(e); }} />
                            <PVASelect options={reportOptions} selected={this.state.reportType} onChange={(e: any) => { this.reportChange(e); }} />
                            {this.state.miniLoad && <LoadingIndicatorMini />}
                        </PVAPage.Header.Title.Text>
                    </PVAPage.Header.Title>
                    <PVAPage.Header.Buttons>
                        {!this.isQuadrantType(this.state.reportType) && this.ActionsButton()}
                    </PVAPage.Header.Buttons>
                </PVAPage.Header>

                <PVAPage.Body>
                    {this.state.reportType === 0 &&  <div className="pva-welcome">
                        <span>Please select a team and a report type</span>
                    </div>}
                    {this.isQuadrantType(this.state.reportType) && this.state.selectedCharacteristic.length >= 3 &&
                        <QuadrantGrid UserStore={this.props.UserStore} FilterStore={this.props.FilterStore}  data={data} one={{ label: this.state.selectedCharacteristic[0].name }} two={{ label: this.state.selectedCharacteristic[1].name }} three={{ label: this.state.selectedCharacteristic[2].name }} four={{ label: this.state.selectedCharacteristic[3].name }} />
                    }
                    {this.state.reportType === TeamGridReport.ReportTypes.CustomScaleComparison &&
                        <>
                            <form>
                                <div className="form-group">
                                    <label>X-Axis: </label>
                                    <PVACharacteristicSelect showAll={true} options={this.state.characteristics} groups={this.state.characteristicGroups} selected={this.state.customScaleX} onChange={(e: any) => this.setCustomX(e)} />
                                </div>
                                <br />
                                <div className="form-group">
                                    <label>Y-Axis: </label>
                                    <PVACharacteristicSelect showAll={true} options={this.state.characteristics} groups={this.state.characteristicGroups} selected={this.state.customScaleY} onChange={(e: any) => this.setCustomY(e)} />
                                </div>
                            </form>
                            <br />
                            <SecondaryScale listView={this.state.listView} UserStore={this.props.UserStore} FilterStore={this.props.FilterStore} data={customScaleData} one={{ label: this.getCustomeLabel(this.state.customScaleX) }} two={{ label: this.getCustomeLabel(this.state.customScaleY) }} loading={this.state.miniLoad} />
                        </>
                    }
                    {this.state.reportType > 0 && <div>
                        <SecondaryScale listView={this.state.listView} UserStore={this.props.UserStore} FilterStore={this.props.FilterStore} data={secondaryData} one={{ label: this.SecondaryScaleLabelOne() }} two={{ label: this.SecondaryScaleLabelTwo() }} loading={this.state.miniLoad} />
                    </div>}
                </PVAPage.Body>
                {!this.isSecondaryScaleReport() && <PVAPage.Footer />}
            </PVAPage>);
    }

    private isSecondaryScaleReport() {
        return this.state.reportType === TeamGridReport.ReportTypes.CustomScaleComparison || this.state.reportType > 0;
    }

    private getCustomQuadrantIdMapping() : object {
        var teamGridReportIdAssignments = new Object();
        var nextGrid = 1;
        this.surveyVersion!.teamGrids.forEach(tg => {
            var nextGridString = '-1.' + nextGrid++;
            var nextGridNumber = parseFloat(nextGridString);
            teamGridReportIdAssignments[nextGridNumber] = tg;
        });

        return teamGridReportIdAssignments;
    }

    private setCustomX(e: any) {
        var characteristicId: number = Number(e.target.value);
    
        if (characteristicId > 0) {
            this.setState(
                {
                    customScaleX: characteristicId
                }
            );
        }      
    }

    private setCustomY(e: any) {
        var characteristicId: number = Number(e.target.value);
    
        if (characteristicId > 0) {
            this.setState(
                {
                    customScaleY: characteristicId
                }
            );
        }      
    }

    private getCustomeLabel(id: number) {
        const characteristic = this.state.characteristics.find((x) => {
            return x.id === id;
        });

        return characteristic ? characteristic.name.toUpperCase() : '';
    }

    private SecondaryScaleLabelOne() {
        if (this.state.reportType > 0) {
            var gridOpt = this.state.gridOptions.find((go: GridOption) => {
                return go.id === this.state.reportType;
            });
            if (gridOpt) {
                return gridOpt.characteristicOneLabel;
            }
        }
        return '';
    }

    private SecondaryScaleLabelTwo() {
        if (this.state.reportType > 0) {
            var gridOpt = this.state.gridOptions.find((go: GridOption) => {
                return go.id === this.state.reportType;
            });
            if (gridOpt) {
                return gridOpt.characteristicTwoLabel;
            }
        }
        return '';
    }
}



// WEBPACK FOOTER //
// ./src/components/reports/teamgrid/TeamGridReport.tsx